import { Metamodel, DataFlowDSLAdapterRegistry, Owned } from 'shell-models-lib';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { DataFlowSink } from 'app/model/dataflow/dataflow-sink';
import { FileBasedSettings } from 'app/model/dataflow/dataset-settings/file-based-settings';
import { IcebergSettings, TimeTravelOption } from 'app/model/dataflow/dataset-settings/iceberg-settings';
import { FileBasedSettingsAdapter } from './file-based-settings.adapter';
import { DataFlowExpressionUtils } from '../expression.util';
import { TimeStampDataFlowType, StringDataFlowType, BooleanDataFlowType } from 'app/model/dataflow/dataflow-datatype';
import { isTruthy } from 'app/model/dataflow/dataflow-expression-utilities';

@DataFlowDSLAdapterRegistry.adapter({
    model: Metamodel.modelFor(IcebergSettings)
})  
export class IcebergFormatAdapter extends FileBasedSettingsAdapter {
    protected _generateSetting(owner: DataFlowSink | DataFlowSource): FileBasedSettings {
        return Owned.create<IcebergSettings>(IcebergSettings.MODEL, owner);
    }
    protected async toSourceDSL(settings: IcebergSettings): Promise<string[]> {
        const props = [];
        props.push(...(await super.toSourceDSL(settings)));
        this._commonPropertiesToDSL(settings, props);
        return props;
    }

    protected async toSinkDSL(settings: IcebergSettings): Promise<string[]> {
        const props = [];
        props.push(...(await super.toSinkDSL(settings)));
        this._commonPropertiesToDSL(settings, props);        
        return props;
    }

    protected async toSourceModel(owner: DataFlowSource, configs: Map<string, string>, handledConfigs: string[]): Promise<IcebergSettings> {
        const icebergSettings = (await super.toSourceModel(owner, configs, handledConfigs)) as IcebergSettings;
        this._commonPropertiesToModel(owner, icebergSettings, configs, handledConfigs);
        return icebergSettings;
    }

    protected async toSinkModel(owner: DataFlowSink, configs: Map<string, string>, handledConfigs: string[]): Promise<IcebergSettings> {
        const icebergSettings = await (super.toSinkModel(owner, configs, handledConfigs)) as IcebergSettings;
        this._commonPropertiesToModel(owner, icebergSettings, configs, handledConfigs);
        return icebergSettings;
    }
    
    private _commonPropertiesToDSL(settings: IcebergSettings, props: string[]): void {

        if (isTruthy(settings.overwrite)) {
            props.push(`overwrite: ${DataFlowExpressionUtils.wrapExpressionConfigValue(settings.overwrite, BooleanDataFlowType.TYPE)}`);

        }

        if (settings.timeTravelOption === TimeTravelOption.Timestamp && settings.timestampAsOf.expression) {
            props.push(
                `timestamp: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    settings.timestampAsOf,
                    TimeStampDataFlowType.TYPE
                )}`
            );
        }
        if (settings.timeTravelOption === TimeTravelOption.SnapshotId && settings.snapshotId.expression) {
            props.push(
                `snapshotId: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    settings.snapshotId,
                    StringDataFlowType.TYPE
                )}`
            );
        }

        if (settings.writeFormat.expression) {
            props.push(
                `writeFormat: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    settings.writeFormat,
                    StringDataFlowType.TYPE
                )}`
            );
        }
    }

    private _commonPropertiesToModel(owner: DataFlowSource | DataFlowSink, settings: IcebergSettings, configs: Map<string, string>, handledConfigs: string[]): void {

        if (configs.has('overwrite')) {
            settings.overwrite = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('overwrite'),
                owner,
                BooleanDataFlowType.TYPE
            );
            handledConfigs.push('overwrite');
        }

        if (configs.has('timestamp')) {
            settings.timestampAsOf = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('timestamp'),
                owner,
                TimeStampDataFlowType.TYPE
            );
            settings.timeTravelOption = TimeTravelOption.Timestamp;
            handledConfigs.push('timestamp');
        }

        if (configs.has('snapshotId')) {
            settings.snapshotId = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('snapshotId'),
                owner,
                StringDataFlowType.TYPE
            );
            settings.timeTravelOption = TimeTravelOption.SnapshotId;
            handledConfigs.push('snapshotId');
        }

        if (configs.has('writeFormat')) {
            settings.writeFormat = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('writeFormat'),
                owner,
                StringDataFlowType.TYPE
            );
            handledConfigs.push('writeFormat');
        }

    }
}
